/*
 * accountsFormat — tiny shared helpers for the /accounts tab panels.
 * Formatting only; no fetching, no state.
 */

/**
 * Compact money: $1.2B / $3.4M / $560K / $12
 *
 * The unit is chosen from the ROUNDED figure, not the raw one. Choosing it
 * first is the obvious way to write this and it is wrong at every boundary:
 * $999,996 is under a million, so it took the K unit and then rounded to
 * `$1000K` — the right number in the wrong unit, sitting in a list directly
 * above a `$1.9M`, where it reads as half of what it is. $999,999,999 did the
 * same as `$1000M`. Seen on Secretary of State's purchase history, 2026-09-18.
 */
const MONEY_TIERS = [[1e9, 'B', 1], [1e6, 'M', 1], [1e3, 'K', 0], [1, '', 0]];

export function fmtMoney(value) {
  const n = Number(value) || 0;
  for (let i = 0; i < MONEY_TIERS.length; i += 1) {
    const [floor, suffix, dp] = MONEY_TIERS[i];
    const last = i === MONEY_TIERS.length - 1;
    if (Math.abs(n) < floor && !last) continue;
    const text = (n / floor).toFixed(dp);
    // Rounding can carry a value up out of the tier that was picked for it.
    if (Math.abs(Number(text)) >= 1000 && i > 0) {
      const [upFloor, upSuffix, upDp] = MONEY_TIERS[i - 1];
      return `$${(n / upFloor).toFixed(upDp)}${upSuffix}`;
    }
    return `$${text}${suffix}`;
  }
  return `$${n.toFixed(0)}`;
}

/**
 * Money off a "top vendors" / "top categories" row, whatever the endpoint
 * chose to call the column.
 *
 * `/api/v1/procurement/summary` sends `{ vendor, spend, spendFormatted }`, and
 * both readers asked for `total` / `totalFormatted`. Neither exists, so every
 * row rendered `fmtMoney(undefined)` — a literal `$0` beside every vendor and
 * every category, on every account. The bug was invisible in review precisely
 * because `$0` looks like data.
 *
 * `total` stays in the lookup because the sankey drilldown rows do use it;
 * this reads whichever the caller actually has rather than betting on one.
 */
export function rowSpend(row) {
  if (!row) return 0;
  const raw = row.spend != null ? row.spend : row.total;
  return Number(raw) || 0;
}

/** Server-formatted money for such a row, else our own. See `rowSpend`. */
export function rowSpendText(row) {
  return (row && (row.spendFormatted || row.totalFormatted)) || fmtMoney(rowSpend(row));
}

/**
 * Backend account identifier for this department — acronym first (the
 * canonical `dept` vocabulary), department_id as fallback. Matches how
 * AccountWorkspace keys TechMap and notes.
 */
export function accountKey(selectedDepartment) {
  return (
    selectedDepartment?.acronym ||
    (selectedDepartment?.department_id != null
      ? String(selectedDepartment.department_id)
      : '')
  );
}

/**
 * "FY25" from a fiscal-year START year (2024).
 *
 * A fiscal year is named by the year it ENDS in, and never hyphenated (Ryan,
 * 2026-09-16). This took a start year and rendered the span, so the SAME year
 * appeared as "FY 24-25" here and "FY25" on every chart — and, worse, "FY25-26"
 * elsewhere meant a DIFFERENT year than "FY25" did. One name per year now.
 */
export function fmtFyStart(year) {
  const y = Number(year);
  if (!Number.isFinite(y)) return '';
  return `FY${String((y + 1) % 100).padStart(2, '0')}`;
}

/* The spend trend is bucketed on `mv_vendor_analysis_facts.sfy_year`, which is
 * baked to California's July boundary, so completeness is judged on that
 * calendar and not the viewer's basis. */
const CA_FY_START_MONTH = 7;

/**
 * "FY27" → 2027, the fiscal year's ENDING calendar year.
 *
 * The legacy span form "FY26-27" is still read — a label can outlive a deploy
 * in a cached query or an open tab, and the ending year is the LAST group
 * either way.
 */
export function fyEndYear(label) {
  const m = /^FY(\d{2})(?:-(\d{2}))?$/.exec(String(label || '').trim());
  return m ? 2000 + Number(m[2] ?? m[1]) : null;
}

/** The ending calendar year of the fiscal year we are currently inside. */
export function currentFyEndYear(now = new Date()) {
  return now.getMonth() + 1 >= CA_FY_START_MONTH ? now.getFullYear() + 1 : now.getFullYear();
}

/**
 * Is this fiscal-year label a year that has not finished yet?
 *
 * The account board's headline delta used to divide the newest bucket by the
 * one before it with no completeness test. On 11 Sep 2026 — 2.4 months into
 * FY26-27 — that compared 2.4 months against 12 and rendered "▼85%" for FTB
 * and "▼99%" for CDT, in red, as the most prominent figure on the board. It
 * fires on every account for roughly ten months of every year.
 */
export function isPartialFy(label, now) {
  const end = fyEndYear(label);
  return end != null && end >= currentFyEndYear(now);
}

/**
 * A fiscal year narrowed to the part the time filter actually covers.
 *
 * WHY. The Palo Alto footprint honours the topbar window, so its LEADING
 * column is a partial fiscal year — on CDT at 3y, FY24 is counted from
 * 2023-09-17 rather than 2023-07-01 and the cell reads $67K. Its drill opened
 * the WHOLE fiscal year and showed $864K, under a footer promising "the list
 * below should reconcile to it". Thirteen times the cell, on the one column
 * where the two disagreed: FY25 and FY26 sit wholly inside the window and
 * reconciled exactly, which is what made this survive.
 *
 * Both ends clip. The trailing column is partial too — a window ending
 * 2026-09-16 cuts FY27 off at eleven months.
 *
 * ISO day strings compare correctly as strings, which is why these are
 * compared rather than parsed: a Date round-trip here would reintroduce the
 * timezone slip the ISO_DAY guard elsewhere in this file exists to prevent.
 *
 * @param range {start, end} the fiscal year, ISO days
 * @param window {startDate, endDate} the filter, either end nullable
 * @returns {start, end, clipped} — `clipped` when either end moved, so the
 *          caller can SAY the figure is a part-year rather than let a reader
 *          compare it against a full one.
 */
export function clipRangeToWindow(range, window) {
  const fyStart = range && range.start;
  const fyEnd = range && range.end;
  const winStart = window && window.startDate;
  const winEnd = window && window.endDate;
  const start = winStart && fyStart && winStart > fyStart ? winStart : fyStart;
  const end = winEnd && fyEnd && winEnd < fyEnd ? winEnd : fyEnd;
  return { start, end, clipped: start !== fyStart || end !== fyEnd };
}

/**
 * The fiscal-year span a chart ACTUALLY DREW, as a label — "FY23–FY27", or
 * "FY24" when it drew one year, or null when it drew nothing.
 *
 * WHY THIS IS A FUNCTION AND NOT A SENTENCE IN A CAPTION. On 2026-09-17 the
 * spend-trajectory caption read "every year on file, not the time filter".
 * `/vendor-trajectory` defaults to FIVE fiscal years and FTB has TWELVE on
 * file — FY16 through FY27 — so seven years were missing, including FY21's
 * $57.0M, and the caption told the reader nothing was. The constant was typed
 * once and never re-checked against the query underneath it; a label read off
 * the rows that were rendered cannot drift from them.
 *
 * Takes whatever the caller has: numeric years (2025), FY labels ("FY25",
 * "FY2025"), or row objects carrying `fy` or `year`. Anything unreadable is
 * dropped rather than guessed at — a garbage year in a span label is the same
 * class of lie this exists to prevent.
 */
export function fiscalSpanLabel(values) {
  const asYear = (v) => {
    const raw = v && typeof v === 'object' ? (v.fy ?? v.year) : v;
    if (typeof raw === 'number') return Number.isFinite(raw) ? normalizeFyYear(raw) : null;
    const m = /^FY?(\d{2}|\d{4})$/i.exec(String(raw == null ? '' : raw).trim());
    return m ? normalizeFyYear(Number(m[1])) : null;
  };
  const years = (Array.isArray(values) ? values : []).map(asYear).filter((n) => n != null);
  if (!years.length) return null;
  const lo = Math.min(...years);
  const hi = Math.max(...years);
  return lo === hi ? fyShortLabel(lo) : `${fyShortLabel(lo)}\u2013${fyShortLabel(hi)}`;
}

/**
 * ENDING year to label — the inverse of `fyEndYear`, and NOT `fmtFyStart`.
 *
 * `fmtFyStart` takes a fiscal year's STARTING year and adds one (2024 gives
 * "FY25"). Everything this function is fed — `sfy_year`, the bump chart's
 * `year`, the trajectory's `fy` — names a fiscal year by the year it ENDS in,
 * so routing those through `fmtFyStart` would label every span one year late:
 * FY23-FY27 drawn as "FY24\u2013FY28". Caught before it shipped only because
 * this is a function with a test rather than an expression in a caption.
 */
function fyShortLabel(endYear) {
  return `FY${String(endYear % 100).padStart(2, '0')}`;
}

/** Two digits mean this century; four mean themselves. */
function normalizeFyYear(n) {
  if (!Number.isFinite(n)) return null;
  return n < 100 ? 2000 + n : n;
}

/** "FY25" from a "2425"-style fiscal-year code. */
export function fmtFyCode(fy) {
  if (!fy) return '';
  return fmtFyStart(2000 + Number(String(fy).slice(0, 2)));
}

/**
 * The global time filter as the date window the v1 account endpoints take.
 * Both ends, and only ever a real `YYYY-MM-DD` — an unreadable value becomes
 * null so the caller omits the param and the backend's own floor applies.
 *
 * WHY DATES AND NOT `sinceYear`. `sinceYear` can only floor at January 1st,
 * but the footprint buckets by FISCAL year: at a 3y filter it admitted Jan-Jun
 * of a fiscal year that began fourteen months before the window, and the
 * heatmap drew it as a leading column holding a few weeks of spend — the
 * "near-empty column" that reads as a rendering fault and is not one. On the
 * PANW basis the leak is seven months. It also had no upper bound at all, so a
 * bounded custom range (2019-2021) came back with every year through today.
 * `dateRange` is the canonical axis (frontend invariant 7); send it.
 *
 * WHY THIS IS SHARED RATHER THAN INLINE. `accounts-panw-footprint` is queried
 * from TWO places — AccountsDashTab (the Key numbers cards read its fiscal
 * years) and the board's footprint widget — under the same react-query key.
 * Deriving the window in only one of them, or deriving it two subtly different
 * ways, gives the two callers different keys: they stop sharing the cache, the
 * page fetches the same endpoint twice, and the card and the heatmap disagree
 * about which years exist. One function, imported by both.
 */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
export function footprintWindow(dateRange) {
  const day = (v) => {
    const t = String(v == null ? '' : v).trim();
    return ISO_DAY.test(t) ? t : null;
  };
  return { startDate: day(dateRange?.startDate), endDate: day(dateRange?.endDate) };
}

/**
 * Does the active time window contain the WHOLE of a fiscal year?
 *
 * The Key numbers Palo Alto card reads the FOOTPRINT's query so the two always
 * agree — but that query carries the topbar's window, and a window clips the
 * fiscal years at its edges. At 1y on CA State Library the card read
 * "FY26 full year $112K" when FY26's actual total is $140K: $112K is the slice
 * of FY26 inside a twelve-month window. The IT spend card beside it reads an
 * UNFILTERED series, so the identical phrase meant a real year on the left and
 * a clipped one on the right.
 *
 * Exported, not inlined, because the first attempt passed `fyLabelOf(fy)`
 * ("FY26") to `fiscalYearRange`, which takes the NUMERIC year. It returned a
 * range that compared wrong and the card claimed "in view" at 3y — a window
 * that contains FY26 whole. A closure could not be tested; this can.
 *
 * @param fy numeric fiscal year, e.g. 2026
 * @param window {startDate, endDate} ISO days, either end nullable
 * @param calendar fiscal calendar row
 * @param rangeOf (fy, calendar) -> {start, end} ISO days
 */
export function windowCoversFiscalYear(fy, window, calendar, rangeOf) {
  const n = Number(fy);
  if (!Number.isFinite(n) || !calendar || typeof rangeOf !== 'function') return true;
  const startDate = window?.startDate || null;
  const endDate = window?.endDate || null;
  // No window is the all-time case: every year is whole.
  if (!startDate && !endDate) return true;
  let r;
  try { r = rangeOf(n, calendar); } catch { return true; }
  if (!r || !r.start || !r.end) return true;
  if (startDate && r.start < startDate) return false;
  if (endDate && r.end > endDate) return false;
  return true;
}
